"use client";

import Image from "next/image";
import styles from "./heroGradientText.module.scss";
import { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import SplitType from "split-type";
import { gsap } from "gsap";
import RoundedButton from "../roundedButton/roundedButton";
const Lottie = dynamic(() => import("lottie-react"), { ssr: false });

function HeroGradientText() {
  const [isOutside, setIsOutside] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const movingBubble = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLHeadingElement>(null);
  const bgImageRef = useRef<HTMLImageElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Handle image loading
  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    if (!isLoaded) return;

    // Make content visible before animation
    if (contentRef.current) {
      gsap.set(contentRef.current, { visibility: "visible" });
    }

    // Initialize SplitType for subtitle
    const subtitle = new SplitType(subtitleRef.current, { types: "words" });

    // Create main timeline
    const mainTl = gsap.timeline({
      defaults: {
        ease: "power2.out",
        duration: 1.2,
      },
    });

    // Build the animation sequence
    mainTl
      // Fade in and scale the background
      .to(bgImageRef.current, {
        scale: 1,
        opacity: 1,
        duration: 1.8,
        ease: "power1.inOut",
      })
      // Animate words with slide from left effect
      .fromTo(
        subtitle.words,
        {
          opacity: 0,
          x: 100,
          rotateY: -15,
        },
        {
          opacity: 1,
          x: 0,
          rotateY: 0,
          duration: 1,
          stagger: 0.6,
          ease: "power2.out",
        },
        "-=0.1",
      )
      // Animate the button
      .fromTo(
        buttonRef.current,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
        },
        "-=0.5",
      );

    // Cleanup function
    return () => {
      if (subtitle) subtitle.revert();
    };
  }, [isLoaded]);

  useEffect(() => {
    const movingCircle = movingBubble.current;
    let curX = 0;
    let curY = 0;
    let tgX = 0;
    let tgY = 0;

    if (!movingCircle) {
      return;
    } else {
      function move() {
        curX += (tgX - curX) / 20;
        curY += (tgY - curY) / 20;
        const halfWidth = movingCircle!.offsetWidth / 2;
        const halfHeight = movingCircle!.offsetHeight / 2;
        movingCircle!.style.transform = `translate(${Math.round(
          curX - halfWidth,
        )}px, ${Math.round(curY - halfHeight)}px)`;
        requestAnimationFrame(() => {
          move();
        });
      }
      window.addEventListener("mousemove", (event) => {
        tgX = event.clientX;
        tgY = event.clientY;
      });
      move();
    }
  }, []);

  useEffect(() => {
    const heroElement = heroRef.current;
    if (!heroElement) return;

    const handleMouseEnter = () => setIsOutside(false);
    const handleMouseLeave = () => setIsOutside(true);

    // Check if mouse is already inside the element on initial load
    const checkInitialMousePosition = (e: MouseEvent) => {
      const rect = heroElement.getBoundingClientRect();
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        setIsOutside(false);
      }
      window.removeEventListener("mousemove", checkInitialMousePosition);
    };

    window.addEventListener("mousemove", checkInitialMousePosition, {
      once: true,
    });

    heroElement.addEventListener("mouseenter", handleMouseEnter);
    heroElement.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      heroElement.removeEventListener("mouseenter", handleMouseEnter);
      heroElement.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("mousemove", checkInitialMousePosition);
    };
  }, []);

  return (
    <div className={`${styles["heroGradientText"]}`} ref={heroRef}>
      <div
        className={`${styles["heroGradientText__content"]}`}
        ref={contentRef}
      >
        <div className={`${styles["heroGradientText__content-text"]}`}>
          <h1>
            <div className={`${styles.animation} font-size-140`}>
              <span className={`${styles.first}`}>QR Code</span>
              <span className={`${styles.oh}`}>
                <span className={`${styles.second}`}>Solutions</span>
              </span>
            </div>
          </h1>
          <h2 className="font-size-40" ref={subtitleRef}>
            Fast. Reliable. Customizable.
          </h2>
          <div
            className={`${styles["heroGradientText__button"]} d-none-md d-flex-xs`}
          >
            <RoundedButton
              title="Start for free"
              url={`${process.env.NEXT_PUBLIC_DASHBOARD_URL}/account/create`}
              light={false}
              fill={true}
              target="_blank"
            />
          </div>
        </div>

        <Image
          className={`${styles["heroGradientText__bg"]}`}
          src="/images/qr-pattern.svg"
          alt="QR Code Solutions"
          width={1000}
          height={1000}
          ref={bgImageRef}
          onLoad={handleImageLoad}
          priority
        />
        <div
          className={`${styles["heroGradientText__interactive-gradient-container"]}`}
        >
          <div
            ref={movingBubble}
            className={`${styles["heroGradientText__interactive-gradient-container--interactive-circle"]}`}
            style={{
              opacity: isOutside ? 0 : 1,
              transition: "opacity 0.3s ease",
            }}
          ></div>
          <div
            className={`${styles["heroGradientText__interactive-gradient-container--auto-circle"]}`}
          ></div>
        </div>
        <div className="custom-container">
          <div className="inner">
            <div
              className={`${styles["heroGradientText__footer"]}`}
              ref={buttonRef}
            >
              <div className={`${styles["heroGradientText__mouse-icon"]}`}>
                <Lottie
                  animationData={require("/public/images/mouseAnimation.json")}
                  loop={true}
                  style={{ width: 40, height: 40 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroGradientText;
